/**
 * Pricing Configuration Loader
 *
 * @description Loads energy tariff pricing from JSON configuration files
 * @version 2.0.0
 * @created 2025-12-19
 * @updated 2025-01-05 - Multi-OSD support
 *
 * This module provides:
 * - Multi-OSD pricing configuration (Tauron, PGE, Enea, Energa)
 * - JSON-based pricing configuration for each OSD and year
 * - Backward-compatible exports matching original constants
 * - Easy addition of new years/OSD (just add a JSON file)
 * - Type-safe access to pricing data
 *
 * To add new OSD pricing:
 * 1. Create pricing-[osd]-[year].json in config/
 * 2. Import and add to PRICING_BY_OSD_AND_YEAR below
 *
 * To add new year for existing OSD:
 * 1. Copy existing OSD pricing JSON to new year
 * 2. Update the year and values
 * 3. Import and add to PRICING_BY_OSD_AND_YEAR
 */

// Import OSD types
import { OSDCode, TariffCode, OSD_CODES, TARIFF_AVAILABILITY } from './osd-types';

// Import Tauron pricing
import pricingTauron2025 from '../config/pricing-tauron-2025.json';
import pricingTauron2026 from '../config/pricing-tauron-2026.json';

// Import PGE pricing
import pricingPge2025 from '../config/pricing-pge-2025.json';
import pricingPge2026 from '../config/pricing-pge-2026.json';

// Import Enea pricing
import pricingEnea2025 from '../config/pricing-enea-2025.json';
import pricingEnea2026 from '../config/pricing-enea-2026.json';

// Import Energa pricing
import pricingEnerga2025 from '../config/pricing-energa-2025.json';
import pricingEnerga2026 from '../config/pricing-energa-2026.json';

// Import OSD metadata
import osdMetadata from '../config/osd-metadata.json';

// Re-export OSD types for consumers
export type { OSDCode, TariffCode } from './osd-types';
export { OSD_CODES, TARIFF_AVAILABILITY } from './osd-types';
export type { OSDPricingConfig, OSDOperator, OSDMetadata } from './osd-types';

// Type definitions for JSON config structure
export interface PricingFees {
  handlingFee: {
    regulated: number;
    commercialBasic: number;
    commercialPremium: number;
    description: string;
  };
  ozeFee: {
    perKwh: number;
    perMwh: number;
    description: string;
  };
  cogenerationFee: {
    perKwh: number;
    perMwh: number;
    description: string;
  };
  subscriptionFee: {
    monthly: number;
    description: string;
  };
  transitionFee: {
    tier1: number;
    tier2: number;
    tier3: number;
    description: string;
  };
  powerFee: {
    tier1: number;
    tier2: number;
    tier3: number;
    tier4: number;
    tierThresholds: {
      tier1Max: number;
      tier2Max: number;
      tier3Max: number;
    };
    frozenPeriod: {
      startMonth: number;
      endMonth: number;
      year: number;
    } | null;
    description: string;
  };
}

export interface TariffPricing {
  name: string;
  description: string;
  energyPricePerKwh?: number;
  energyDayPricePerKwh?: number;
  energyNightPricePerKwh?: number;
  distributionPerKwh?: number;
  distributionDayPerKwh?: number;
  distributionNightPerKwh?: number;
  distributionWeekendPerKwh?: number;
  distributionPeakPerKwh?: number;
  distributionOffPeakPerKwh?: number;
  distributionGreenPerKwh?: number;
  distributionLightGreenPerKwh?: number;
  distributionYellowPerKwh?: number;
  distributionRedPerKwh?: number;
  avgDistributionPerKwh?: number;
  fixedMonthlyFee: number;
  qualityCharge: number;
  dayHours?: { start: number; end: number };
  peakHours?: {
    morning: { start: number; end: number };
    eveningWinter: { start: number; end: number };
    eveningSummer: { start: number; end: number };
  };
  solarHours?: {
    summer: { start: number; end: number };
    winter: { start: number; end: number };
  };
}

export interface YearlyPricingConfig {
  year: number;
  description: string;
  source: string;
  vatRate: number;
  changes?: Record<string, string>;
  fees: PricingFees;
  tariffs: {
    g11: TariffPricing;
    g12: TariffPricing;
    g12w: TariffPricing;
    g12as: TariffPricing;
    g13: TariffPricing;
    g13s: TariffPricing;
    g14?: TariffPricing;
  };
}

// ============================================================================
// PRICING REGISTRY - MULTI-OSD
// ============================================================================

/**
 * Multi-dimensional pricing registry by OSD and year
 * Structure: PRICING_BY_OSD_AND_YEAR[OSDCode][year]
 */
const PRICING_BY_OSD_AND_YEAR: Record<OSDCode, Record<number, YearlyPricingConfig>> = {
  TAURON: {
    2025: pricingTauron2025 as unknown as YearlyPricingConfig,
    2026: pricingTauron2026 as unknown as YearlyPricingConfig,
  },
  PGE: {
    2025: pricingPge2025 as unknown as YearlyPricingConfig,
    2026: pricingPge2026 as unknown as YearlyPricingConfig,
  },
  ENEA: {
    2025: pricingEnea2025 as unknown as YearlyPricingConfig,
    2026: pricingEnea2026 as unknown as YearlyPricingConfig,
  },
  ENERGA: {
    2025: pricingEnerga2025 as unknown as YearlyPricingConfig,
    2026: pricingEnerga2026 as unknown as YearlyPricingConfig,
  },
};

/**
 * Backward compatible: Pricing by year (defaults to TAURON)
 * Kept for internal reference - use getPricingConfig() for access
 * @deprecated Use PRICING_BY_OSD_AND_YEAR instead
 * @internal
 */
export const PRICING_BY_YEAR: Record<number, YearlyPricingConfig> = PRICING_BY_OSD_AND_YEAR.TAURON;

/**
 * Default OSD if not specified
 */
export const DEFAULT_OSD: OSDCode = 'TAURON';

/**
 * Default year if not specified
 * Updated to 2026 (January 2026 - unfrozen prices)
 */
export const DEFAULT_PRICING_YEAR = 2026;

/**
 * Available OSD codes
 */
export const AVAILABLE_OSDS: readonly OSDCode[] = OSD_CODES;

/**
 * Get available years for a specific OSD
 */
export function getAvailableYearsForOSD(osd: OSDCode = DEFAULT_OSD): number[] {
  return Object.keys(PRICING_BY_OSD_AND_YEAR[osd] || {}).map(Number).sort();
}

/**
 * Available pricing years (default: TAURON)
 */
export const AVAILABLE_YEARS = getAvailableYearsForOSD(DEFAULT_OSD);

/**
 * Latest available year (default: TAURON)
 */
export const LATEST_YEAR = Math.max(...AVAILABLE_YEARS);

/**
 * Get OSD metadata
 */
export function getOSDMetadata() {
  return osdMetadata;
}

/**
 * Get available tariffs for an OSD
 */
export function getAvailableTariffsForOSD(osd: OSDCode): TariffCode[] {
  return [...(TARIFF_AVAILABILITY[osd] || [])];
}

// ============================================================================
// LOADER FUNCTIONS - OSD-AWARE
// ============================================================================

/**
 * Get pricing configuration for a specific OSD and year
 * @param osd - OSD code (defaults to TAURON)
 * @param year - Year (defaults to 2025)
 * @returns Yearly pricing configuration
 * @throws Error if OSD/year combination not available
 */
export function getPricingConfigByOSD(
  osd: OSDCode = DEFAULT_OSD,
  year: number = DEFAULT_PRICING_YEAR
): YearlyPricingConfig {
  const osdConfigs = PRICING_BY_OSD_AND_YEAR[osd];
  if (!osdConfigs) {
    throw new Error(
      `OSD ${osd} not available. Available OSDs: ${AVAILABLE_OSDS.join(', ')}`
    );
  }

  const config = osdConfigs[year];
  if (!config) {
    const availableYears = getAvailableYearsForOSD(osd);
    throw new Error(
      `Pricing for ${osd} year ${year} not available. Available years: ${availableYears.join(', ')}`
    );
  }
  return config;
}

/**
 * Get pricing configuration for a specific year (backward compatible - defaults to TAURON)
 * @param year - Year (defaults to 2025)
 * @returns Yearly pricing configuration
 * @throws Error if year not available
 * @deprecated Use getPricingConfigByOSD instead
 */
export function getPricingConfig(year: number = DEFAULT_PRICING_YEAR): YearlyPricingConfig {
  return getPricingConfigByOSD(DEFAULT_OSD, year);
}

/**
 * Get fees for a specific OSD and year
 * @param osd - OSD code (defaults to TAURON)
 * @param year - Year (defaults to 2025)
 * @returns Fee configuration
 */
export function getFeesForOSD(
  osd: OSDCode = DEFAULT_OSD,
  year: number = DEFAULT_PRICING_YEAR
): PricingFees {
  return getPricingConfigByOSD(osd, year).fees;
}

/**
 * Get fees for a specific year (backward compatible - defaults to TAURON)
 * @param year - Year (defaults to 2025)
 * @returns Fee configuration
 * @deprecated Use getFeesForOSD instead
 */
export function getFeesForYear(year: number = DEFAULT_PRICING_YEAR): PricingFees {
  return getFeesForOSD(DEFAULT_OSD, year);
}

/**
 * Get tariff pricing for a specific OSD and year
 * @param osd - OSD code (defaults to TAURON)
 * @param year - Year (defaults to 2025)
 * @param tariff - Tariff type (g11, g12, etc.)
 * @returns Tariff-specific pricing
 */
export function getTariffPricingByOSD(
  osd: OSDCode = DEFAULT_OSD,
  year: number = DEFAULT_PRICING_YEAR,
  tariff: keyof YearlyPricingConfig['tariffs']
): TariffPricing | undefined {
  return getPricingConfigByOSD(osd, year).tariffs[tariff];
}

/**
 * Get tariff pricing for a specific year (backward compatible - defaults to TAURON)
 * @param year - Year (defaults to 2025)
 * @param tariff - Tariff type (g11, g12, etc.)
 * @returns Tariff-specific pricing
 * @deprecated Use getTariffPricingByOSD instead
 */
export function getTariffPricing(
  year: number = DEFAULT_PRICING_YEAR,
  tariff: keyof YearlyPricingConfig['tariffs']
): TariffPricing | undefined {
  return getTariffPricingByOSD(DEFAULT_OSD, year, tariff);
}

/**
 * Get VAT rate for a specific OSD and year
 * @param osd - OSD code (defaults to TAURON)
 * @param year - Year (defaults to 2025)
 * @returns VAT rate (e.g., 0.23 for 23%)
 */
export function getVatRateByOSD(
  osd: OSDCode = DEFAULT_OSD,
  year: number = DEFAULT_PRICING_YEAR
): number {
  return getPricingConfigByOSD(osd, year).vatRate;
}

/**
 * Get VAT rate for a specific year (backward compatible - defaults to TAURON)
 * @param year - Year (defaults to 2025)
 * @returns VAT rate (e.g., 0.23 for 23%)
 * @deprecated Use getVatRateByOSD instead
 */
export function getVatRate(year: number = DEFAULT_PRICING_YEAR): number {
  return getVatRateByOSD(DEFAULT_OSD, year);
}

// ============================================================================
// BACKWARD-COMPATIBLE EXPORTS
// ============================================================================
// These exports match the original tariff-calculator.ts constants
// Note: All use TAURON pricing as the default/legacy behavior

/**
 * VAT Rate (Poland)
 */
export const VAT_RATE = pricingTauron2025.vatRate;

/**
 * Handling Fee Constants (Opłata Handlowa)
 */
export const HANDLING_FEE = {
  REGULATED: pricingTauron2025.fees.handlingFee.regulated,
  COMMERCIAL_BASIC: pricingTauron2025.fees.handlingFee.commercialBasic,
  COMMERCIAL_PREMIUM: pricingTauron2025.fees.handlingFee.commercialPremium,
};

/**
 * OZE Fee 2025 (Opłata OZE)
 */
export const OZE_FEE = pricingTauron2025.fees.ozeFee.perKwh;

/**
 * OZE Fee 2026 (Opłata OZE) - +108%
 */
export const OZE_FEE_2026 = pricingTauron2026.fees.ozeFee.perKwh;

/**
 * Cogeneration Fee 2025 (Opłata Kogeneracyjna)
 */
export const COGENERATION_FEE = pricingTauron2025.fees.cogenerationFee.perKwh;

/**
 * Cogeneration Fee 2026 (Opłata Kogeneracyjna) - +45%
 */
export const COGENERATION_FEE_2026 = pricingTauron2026.fees.cogenerationFee.perKwh;

/**
 * Subscription Fee (Opłata Abonamentowa)
 */
export const SUBSCRIPTION_FEE = pricingTauron2025.fees.subscriptionFee.monthly;

/**
 * Transition Fee (Opłata Przejściowa)
 */
export const TRANSITION_FEE = {
  TIER_1: pricingTauron2025.fees.transitionFee.tier1,
  TIER_2: pricingTauron2025.fees.transitionFee.tier2,
  TIER_3: pricingTauron2025.fees.transitionFee.tier3,
};

/**
 * Power Fee 2025 (Opłata Mocowa)
 */
export const POWER_FEE = {
  TIER_1: pricingTauron2025.fees.powerFee.tier1,
  TIER_2: pricingTauron2025.fees.powerFee.tier2,
  TIER_3: pricingTauron2025.fees.powerFee.tier3,
  TIER_4: pricingTauron2025.fees.powerFee.tier4,
  FROZEN_START_MONTH: pricingTauron2025.fees.powerFee.frozenPeriod?.startMonth ?? 0,
  FROZEN_END_MONTH: pricingTauron2025.fees.powerFee.frozenPeriod?.endMonth ?? 5,
  FROZEN_YEAR: pricingTauron2025.fees.powerFee.frozenPeriod?.year ?? 2025,
};

/**
 * Power Fee 2026 (Opłata Mocowa) - +50%
 */
export const POWER_FEE_2026 = {
  TIER_1: pricingTauron2026.fees.powerFee.tier1,
  TIER_2: pricingTauron2026.fees.powerFee.tier2,
  TIER_3: pricingTauron2026.fees.powerFee.tier3,
  TIER_4: pricingTauron2026.fees.powerFee.tier4,
};

// ============================================================================
// YEAR-AWARE FEE GETTERS
// ============================================================================

/**
 * Get OZE fee for given year
 * @param year - Year (default: 2025)
 * @returns OZE fee in PLN/kWh (net)
 */
export function getOzeFeeForYear(year: number = 2025): number {
  const config = getPricingConfig(year >= 2026 ? 2026 : 2025);
  return config.fees.ozeFee.perKwh;
}

/**
 * Get cogeneration fee for given year
 * @param year - Year (default: 2025)
 * @returns Cogeneration fee in PLN/kWh (net)
 */
export function getCogenerationFeeForYear(year: number = 2025): number {
  const config = getPricingConfig(year >= 2026 ? 2026 : 2025);
  return config.fees.cogenerationFee.perKwh;
}

/**
 * Get power fee based on annual consumption and year
 * @param annualConsumption - Total annual consumption in kWh
 * @param year - Year (default: 2025)
 * @returns Power fee in PLN/month (net)
 */
export function getPowerFeeForConsumption(annualConsumption: number, year: number = 2025): number {
  const config = getPricingConfig(year >= 2026 ? 2026 : 2025);
  const thresholds = config.fees.powerFee.tierThresholds;
  const fees = config.fees.powerFee;

  if (annualConsumption < thresholds.tier1Max) return fees.tier1;
  if (annualConsumption < thresholds.tier2Max) return fees.tier2;
  if (annualConsumption < thresholds.tier3Max) return fees.tier3;
  return fees.tier4;
}

/**
 * Get power fee tier for given year
 * @param tier - Tier number (1-4)
 * @param year - Year (default: 2025)
 * @returns Power fee for tier in PLN/month (net)
 */
export function getPowerFeeTierForYear(tier: number, year: number = 2025): number {
  const config = getPricingConfig(year >= 2026 ? 2026 : 2025);
  const fees = config.fees.powerFee;

  switch (tier) {
    case 1: return fees.tier1;
    case 2: return fees.tier2;
    case 3: return fees.tier3;
    case 4: return fees.tier4;
    default: return fees.tier3;
  }
}

/**
 * Check if power fee is frozen for given month/year
 * @param month - Month (0-indexed, 0=Jan)
 * @param year - Year
 * @returns true if power fee is frozen (0 PLN)
 */
export function isPowerFeeFrozen(month: number, year: number): boolean {
  const config = getPricingConfig(year >= 2026 ? 2026 : 2025);
  const frozen = config.fees.powerFee.frozenPeriod;

  if (!frozen) return false;
  if (year !== frozen.year) return false;

  return month >= frozen.startMonth && month <= frozen.endMonth;
}

// ============================================================================
// OSD PRICING BUILDERS
// ============================================================================

/**
 * Build OSD pricing object from config
 * @param osd - OSD code
 * @param year - Year
 * @returns Pricing object with available tariffs for that OSD
 */
export function buildOSDPricing(osd: OSDCode = DEFAULT_OSD, year: number = 2025) {
  const config = getPricingConfigByOSD(osd, year);
  const fees = config.fees;
  const availableTariffs = getAvailableTariffsForOSD(osd);

  const additionalFees = {
    handlingFee: fees.handlingFee.regulated,
    ozeFee: fees.ozeFee.perKwh,
    cogenerationFee: fees.cogenerationFee.perKwh,
    subscriptionFee: fees.subscriptionFee.monthly,
    transitionFee: fees.transitionFee.tier3,
    powerFee: fees.powerFee.tier3,
  };

  const result: any = {};

  // G11 - always available
  if (availableTariffs.includes('G11') && config.tariffs.g11) {
    result.g11 = {
      energyPricePerKwh: config.tariffs.g11.energyPricePerKwh!,
      distributionPerKwh: config.tariffs.g11.distributionPerKwh!,
      fixedMonthlyFee: config.tariffs.g11.fixedMonthlyFee,
      qualityCharge: config.tariffs.g11.qualityCharge,
      ...additionalFees,
    };
  }

  // G12 - always available
  if (availableTariffs.includes('G12') && config.tariffs.g12) {
    result.g12 = {
      energyDayPricePerKwh: config.tariffs.g12.energyDayPricePerKwh!,
      energyNightPricePerKwh: config.tariffs.g12.energyNightPricePerKwh!,
      distributionDayPerKwh: config.tariffs.g12.distributionDayPerKwh!,
      distributionNightPerKwh: config.tariffs.g12.distributionNightPerKwh!,
      fixedMonthlyFee: config.tariffs.g12.fixedMonthlyFee,
      qualityCharge: config.tariffs.g12.qualityCharge,
      ...additionalFees,
    };
  }

  // G12w - always available
  // Note: Some OSDs (PGE) use Peak/OffPeak naming, others (Tauron) use Day/Night/Weekend
  // We normalize to Day/Night/Weekend format for the tariff calculator
  if (availableTariffs.includes('G12w') && config.tariffs.g12w) {
    const g12wConfig = config.tariffs.g12w as any;
    result.g12w = {
      energyPricePerKwh: config.tariffs.g12w.energyPricePerKwh!,
      // Use Day/Night/Weekend if available, fall back to Peak/OffPeak
      distributionDayPerKwh: config.tariffs.g12w.distributionDayPerKwh ?? g12wConfig.distributionPeakPerKwh,
      distributionNightPerKwh: config.tariffs.g12w.distributionNightPerKwh ?? g12wConfig.distributionOffPeakPerKwh,
      distributionWeekendPerKwh: config.tariffs.g12w.distributionWeekendPerKwh ?? g12wConfig.distributionOffPeakPerKwh,
      fixedMonthlyFee: config.tariffs.g12w.fixedMonthlyFee,
      qualityCharge: config.tariffs.g12w.qualityCharge,
      ...additionalFees,
    };
  }

  // G12as - conditional (Tauron, Enea, Energa)
  if (availableTariffs.includes('G12as') && config.tariffs.g12as) {
    result.g12as = {
      energyPricePerKwh: config.tariffs.g12as.energyPricePerKwh!,
      distributionDayPerKwh: config.tariffs.g12as.distributionDayPerKwh!,
      distributionNightPerKwh: config.tariffs.g12as.distributionNightPerKwh!,
      fixedMonthlyFee: config.tariffs.g12as.fixedMonthlyFee,
      qualityCharge: config.tariffs.g12as.qualityCharge,
      ...additionalFees,
    };
  }

  // G12n - PGE only
  if (availableTariffs.includes('G12n') && (config.tariffs as any).g12n) {
    const g12n = (config.tariffs as any).g12n;
    result.g12n = {
      energyDayPricePerKwh: g12n.energyDayPricePerKwh,
      energyNightPricePerKwh: g12n.energyNightPricePerKwh,
      distributionDayPerKwh: g12n.distributionDayPerKwh,
      distributionNightPerKwh: g12n.distributionNightPerKwh,
      fixedMonthlyFee: g12n.fixedMonthlyFee,
      qualityCharge: g12n.qualityCharge,
      nightHours: g12n.nightHours,
      ...additionalFees,
    };
  }

  // G12r - Energa only
  if (availableTariffs.includes('G12r') && (config.tariffs as any).g12r) {
    const g12r = (config.tariffs as any).g12r;
    result.g12r = {
      energyPricePerKwh: g12r.energyPricePerKwh,
      distributionPeakPerKwh: g12r.distributionPeakPerKwh,
      distributionOffPeakPerKwh: g12r.distributionOffPeakPerKwh,
      fixedMonthlyFee: g12r.fixedMonthlyFee,
      qualityCharge: g12r.qualityCharge,
      ...additionalFees,
    };
  }

  // G13 - Tauron only
  if (availableTariffs.includes('G13') && config.tariffs.g13) {
    result.g13 = {
      energyPricePerKwh: config.tariffs.g13.energyPricePerKwh!,
      distributionPeakPerKwh: config.tariffs.g13.distributionPeakPerKwh!,
      distributionOffPeakPerKwh: config.tariffs.g13.distributionOffPeakPerKwh!,
      fixedMonthlyFee: config.tariffs.g13.fixedMonthlyFee,
      qualityCharge: config.tariffs.g13.qualityCharge,
      ...additionalFees,
    };
  }

  // G13s - Tauron only
  if (availableTariffs.includes('G13s') && config.tariffs.g13s) {
    result.g13s = {
      energyPricePerKwh: config.tariffs.g13s.energyPricePerKwh!,
      distributionPeakPerKwh: config.tariffs.g13s.distributionPeakPerKwh!,
      distributionOffPeakPerKwh: config.tariffs.g13s.distributionOffPeakPerKwh!,
      fixedMonthlyFee: config.tariffs.g13s.fixedMonthlyFee,
      qualityCharge: config.tariffs.g13s.qualityCharge,
      ...additionalFees,
    };
  }

  // G14 - Tauron only
  if (availableTariffs.includes('G14') && config.tariffs.g14) {
    result.g14 = {
      distributionGreenPerKwh: config.tariffs.g14.distributionGreenPerKwh!,
      distributionLightGreenPerKwh: config.tariffs.g14.distributionLightGreenPerKwh!,
      distributionYellowPerKwh: config.tariffs.g14.distributionYellowPerKwh!,
      distributionRedPerKwh: config.tariffs.g14.distributionRedPerKwh!,
      avgDistributionPerKwh: config.tariffs.g14.avgDistributionPerKwh!,
      fixedMonthlyFee: config.tariffs.g14.fixedMonthlyFee,
      qualityCharge: config.tariffs.g14.qualityCharge,
      ...additionalFees,
    };
  }

  return result;
}

/**
 * Build TauronPricing object from config (backward compatible)
 * @param year - Year
 * @returns TauronPricing object matching original format
 * @deprecated Use buildOSDPricing instead
 */
export function buildTauronPricing(year: number = 2025) {
  return buildOSDPricing('TAURON', year);
}

/**
 * Pre-built Tauron 2025 Pricing (backward compatible)
 */
export const TAURON_2025_PRICING = buildTauronPricing(2025);

/**
 * Pre-built Tauron 2026 Pricing (backward compatible)
 */
export const TAURON_2026_PRICING = buildTauronPricing(2026);

/**
 * Get pricing data for a given OSD and year
 * @param osd - OSD code
 * @param year - Year
 * @returns Pricing data for that OSD/year
 */
export function getPricingForOSD(osd: OSDCode, year: number = 2025) {
  return buildOSDPricing(osd, year);
}

/**
 * Get pricing data for a given year (backward compatible - defaults to TAURON)
 * @param year - Year (2025 or 2026)
 * @returns Tauron pricing data for that year
 * @deprecated Use getPricingForOSD instead
 */
export function getPricingForYear(year: number) {
  return buildTauronPricing(year);
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  // OSD-aware loader functions
  getPricingConfigByOSD,
  getFeesForOSD,
  getTariffPricingByOSD,
  getVatRateByOSD,
  getPricingForOSD,
  buildOSDPricing,
  getAvailableYearsForOSD,
  getAvailableTariffsForOSD,
  getOSDMetadata,

  // Backward-compatible loader functions
  getPricingConfig,
  getFeesForYear,
  getTariffPricing,
  getVatRate,
  getPricingForYear,
  buildTauronPricing,

  // Fee getters
  getOzeFeeForYear,
  getCogenerationFeeForYear,
  getPowerFeeForConsumption,
  getPowerFeeTierForYear,
  isPowerFeeFrozen,

  // Constants (backward compatible)
  VAT_RATE,
  HANDLING_FEE,
  OZE_FEE,
  OZE_FEE_2026,
  COGENERATION_FEE,
  COGENERATION_FEE_2026,
  SUBSCRIPTION_FEE,
  TRANSITION_FEE,
  POWER_FEE,
  POWER_FEE_2026,
  TAURON_2025_PRICING,
  TAURON_2026_PRICING,

  // OSD metadata
  AVAILABLE_OSDS,
  DEFAULT_OSD,
  OSD_CODES,
  TARIFF_AVAILABILITY,

  // Metadata
  AVAILABLE_YEARS,
  LATEST_YEAR,
  DEFAULT_PRICING_YEAR,
};
